import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { RecurringFamilyCTA } from '../components/RecurringFamilyCTA';

const ESCALADE_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/36e99a6c68af7aa56e11f20fa0ba9bb87ff0e136fd1009eb4f06558841de308b.png";
const MERCEDES_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/2b67039dea220f49085a0ea0e70669c526bf6f66f0f4ac4148612b2ba893f9fe.png";
const SUBURBAN_IMG = "https://static.prod-images.emergentagent.com/jobs/9db8eae7-0fdd-4b64-bee9-a14108c31f8b/images/8a05eda9e292eda4a587a2a8cb272ed1d91d53a5a86837c18f9bda1826d82cd7.jpeg";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/d857d564fcebf897a4230412e907253b7379c3ba2609fee6f2ac79915b42b3e4.png";

const vehicles = [
  {
    name: 'Cadillac Escalade ESV',
    image: ESCALADE_IMG,
    description: 'A flagship luxury SUV offering unmatched comfort, presence, and space. Ideal for airport transfers, corporate travel, and private groups.',
    features: [
      'Seats up to 6 passengers',
      'Tri-zone climate control',
      'Leather interior',
      'Wi-Fi & charging',
      'Bottled water included',
      'Child seat available on request',
    ],
  },
  {
    name: 'Mercedes-Benz S-Class',
    image: MERCEDES_IMG,
    description: 'The pinnacle of luxury sedans. Refined, powerful, and whisper-quiet — designed for executive travel and distinguished arrivals.',
    features: [
      'Seats up to 3 passengers',
      'Executive rear seating with massage',
      'Premium leather interior',
      'Advanced suspension for a smooth ride',
      'Wi-Fi & charging',
      'Bottled water included',
    ],
  },
  {
    name: 'Chevrolet Suburban High Country',
    image: SUBURBAN_IMG,
    description: 'Our flagship full-size SUV in the top-tier High Country trim — spacious, refined, and ideal for families, groups, and long-distance travel in quiet comfort.',
    features: [
      'Seats up to 6 passengers',
      'Large cargo capacity',
      'Leather interior',
      'Wi-Fi & charging',
      'Bottled water included',
    ],
  },
];

export const ChauffeurFleet = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.08, rootMargin: '0px 0px -80px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Cinematic scroll-driven zoom + spec reveal on each vehicle card
  useEffect(() => {
    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            cardObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -100px 0px' }
    );
    document.querySelectorAll('.fleet-card').forEach((el) => cardObserver.observe(el));
    return () => cardObserver.disconnect();
  }, []);

  return (
    <div data-testid="chauffeur-fleet-page" style={{ backgroundColor: '#0D0D0D' }}>

      {/* Hero */}
      <section data-testid="fleet-hero" className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '80vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.42) saturate(0.9) contrast(1.05)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.18) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.7) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(194,168,120,0.05) 0%, transparent 55%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '160px', paddingBottom: '140px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              The Fleet
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="fleet-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(38px, 5.5vw, 64px)', fontWeight: 500, color: '#FAFAFA', marginBottom: '28px', lineHeight: 1.08,
              textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              A Curated Fleet for Comfort, Safety, and Presence
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '17px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.8, maxWidth: '520px', margin: '0 auto' }}>
              Our fleet is intentionally selected to deliver a premium travel experience for every occasion.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '160px', background: 'linear-gradient(to top, #0D0D0D, transparent)' }} />
      </section>

      {/* Vehicles */}
      <section style={{ backgroundColor: '#0D0D0D', padding: '6rem 1.5rem 10rem' }}>
        <div className="max-w-6xl mx-auto space-y-32">
          {vehicles.map((vehicle, index) => (
            <div
              key={vehicle.name}
              data-testid={`fleet-vehicle-${index}`}
              className={`fleet-card grid grid-cols-1 lg:grid-cols-2 items-center fade-element`}
              style={{ gap: '64px' }}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="fleet-image-wrap group">
                  <img
                    src={vehicle.image}
                    alt={vehicle.name}
                    width="800"
                    height="420"
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover"
                    style={{ height: '420px' }}
                  />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(180deg, transparent 50%, rgba(13,13,13,0.5) 100%)',
                  }} />
                  {/* Subtle gold accent corner */}
                  <div className="absolute" style={{
                    top: '20px', left: '20px', width: '32px', height: '1px',
                    background: 'linear-gradient(90deg, rgba(194,168,120,0.55), transparent)',
                  }} />
                  <div className="absolute" style={{
                    top: '20px', left: '20px', width: '1px', height: '32px',
                    background: 'linear-gradient(180deg, rgba(194,168,120,0.55), transparent)',
                  }} />
                </div>
              </div>

              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <p className="fleet-spec" style={{
                  fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '4px',
                  textTransform: 'uppercase', fontWeight: 300, marginBottom: '14px',
                }}>
                  {String(index + 1).padStart(2, '0')} · The Fleet
                </p>
                <h2 className="font-serif fleet-spec delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', fontWeight: 400, color: '#F5F5F5', marginBottom: '16px' }}>
                  {vehicle.name}
                </h2>
                <div className="fleet-gold-line" style={{ marginBottom: '24px' }} />
                <p className="fleet-spec delay-2" style={{ fontSize: '15px', color: 'rgba(204,204,204,0.65)', lineHeight: 1.85, fontWeight: 300, marginBottom: '32px' }}>
                  {vehicle.description}
                </p>

                <p className="fleet-spec delay-3" style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '20px' }}>
                  Specifications
                </p>
                <div className="fleet-spec delay-4" style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '36px' }}>
                  {vehicle.features.map((f, i) => (
                    <div key={i} className="flex items-center" style={{ gap: '12px' }}>
                      <Check size={13} style={{ color: '#C2A878', opacity: 0.6, flexShrink: 0 }} />
                      <span style={{ fontSize: '14px', color: 'rgba(220,220,220,0.7)', fontWeight: 300 }}>{f}</span>
                    </div>
                  ))}
                </div>

                <button
                  className="fleet-spec delay-5"
                  data-testid={`request-vehicle-${index}`}
                  onClick={() => navigate(`/contact?vehicle=${encodeURIComponent(vehicle.name)}#booking-form`)}
                  style={{
                    backgroundColor: '#C2A878', color: '#0B0B0B', padding: '12px 28px',
                    fontSize: '13px', fontWeight: 400, letterSpacing: '0.8px', textTransform: 'uppercase',
                    border: 'none', cursor: 'pointer', transition: 'background-color 300ms ease',
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = '#BFA77A'; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; }}
                >
                  Request This Vehicle
                </button>
              </div>
            </div>
          ))}

          {/* ── Recurring Family Schedule (compact CTA) ── */}
          <div className="fade-element">
            <RecurringFamilyCTA variant="compact" testIdPrefix="fleet-recurring" />
          </div>
        </div>
      </section>
    </div>
  );
};

// heroZoom keyframe is defined in ChauffeurServices.jsx and shared via global CSS
